import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  Grid,
  Select,
  MenuItem,
  FormControl,
  ListItemText,
  OutlinedInput,
  TextField,
  Autocomplete
} from "@mui/material";
import {
  DrawStage,
  isRole
  // ExpandCollapse,
} from "@toorak/tc-common-fe-sdk";
import { useDispatch, useSelector, useStore, shallowEqual } from "react-redux";
import { useParams } from "react-router-dom";
import { style, useDrawsStyles } from "../Draws.styles";
import { propertFormatter } from "../../../create-loan/loan-lists/DetailsView/ColumnFormatters";
import { isOrigUser } from "../../../utils/AccessManagement";
import { DrawRequestStore } from "../../../stores/drawRequest/drawRequest.interface";
import { RootState } from "../../../stores/rootReducer";
import { getDrawDetailsbyLoanId } from "../../../stores/drawRequest/drawRequest.action";
import { updateDrawSelectedProp } from "../../../stores/drawRequest/drawRequest.reducer";
import { DrawRolesEnum } from "../../../utils/constants";
import { ReadOnlyDrawField } from "./ReadOnlyDrawField";
import { useDraftDrawAutosave } from "./useDraftDrawAutosave";
import {
  buildLoanIdOptions,
  filterLoanIdOptions,
  getLoanIdOptionLabel,
  getLoanIdOptionValue,
  getLoanIdRenderLabel,
  getPropertyAddresses,
  getPropertyRenderAddress,
  getSelectedPropertySelectValue,
  getSyncedPropertySelection,
  isLoanIdOptionEqualToValue,
  resolveLoanIdsFromOption,
  resolvePropertyOption
} from "./drawLoanInformation.helpers";

const LOAN_ID_HEADER = "Originator Loan ID / Toorak Loan ID / Originator Name";

const LoanIdListboxComponent = forwardRef<HTMLUListElement, any>(
  (props, ref) => {
    const { children, ...rest } = props;
    return (
      <ul ref={ref} {...rest}>
        <li
          aria-hidden="true"
          style={{
            backgroundColor: "#f5f5f5",
            padding: "8px 12px",
            fontWeight: 600,
            listStyle: "none",
            cursor: "default"
          }}
        >
          {LOAN_ID_HEADER}
        </li>
        {children}
      </ul>
    );
  }
);
LoanIdListboxComponent.displayName = "LoanIdListboxComponent";

function FieldRow({
  fieldWidth,
  classes,
  testId,
  children
}: Readonly<{
  fieldWidth: any;
  classes: any;
  testId?: string;
  children: React.ReactNode;
}>) {
  return (
    <Grid
      item
      container
      xs={fieldWidth}
      className={classes.fieldRow}
      {...(testId ? { "data-testid": testId } : {})}
    >
      {children}
    </Grid>
  );
}

function PrimaryLoanIdSection({
  isEditable,
  classes,
  availableLoanIds,
  selectedLoanId,
  drawDetails,
  drawObj,
  isSubmitClicked,
  showErrorMsg,
  handleLoanIdChange
}: any) {
  if (!isEditable) {
    return (
      <ReadOnlyDrawField
        classes={classes}
        label="Primary Loan ID"
        value={drawObj?.primaryLoanId}
        valueTestId="primary-loan-value"
        blankTestId="primary-loan-blank"
        headerTestId="draw-primary-loan-id"
      />
    );
  }
  return (
    <Grid>
      <p className={classes.headerLoanID} data-testid="primary-loan-id">
        Primary Loan ID <span className={classes.asterick}>*</span>
      </p>
      <Autocomplete
        id="checkboxes-loanid-demo"
        data-testid="auto-complete-loanid-input"
        options={availableLoanIds}
        getOptionLabel={getLoanIdOptionLabel}
        filterOptions={(options: any[], { inputValue }) =>
          filterLoanIdOptions(options, inputValue)
        }
        onChange={(_event: any, newValue: any) => {
          handleLoanIdChange(newValue);
        }}
        value={getLoanIdOptionValue(
          availableLoanIds,
          selectedLoanId,
          drawDetails?.toorakLoanId
        )}
        isOptionEqualToValue={isLoanIdOptionEqualToValue}
        ListboxComponent={LoanIdListboxComponent}
        slotProps={{
          paper: { style: { minWidth: 420 } }
        }}
        renderOption={(props, option: any) => {
          const { key: _optionKey, ...optionProps } = props;
          return (
            <li
              key={option?.index ?? option?.primaryLoanId}
              {...optionProps}
            >
              {getLoanIdRenderLabel(option)}
            </li>
          );
        }}
        style={style}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder="Select" />
        )}
      />
      {isSubmitClicked ? showErrorMsg("selectedLoanId") : null}
    </Grid>
  );
}

function PropertyDetailsSection({
  isDraft,
  classes,
  selectedLoanId,
  selectedProperty,
  selectedPropertyValue,
  propertyDetailsOptions,
  isSubmitClicked,
  showErrorMsg,
  handlePropertyChange,
  getPropertyView
}: any) {
  if (!isDraft) {
    return (
      <div className={classes.itemBox}>
        <p
          className={classes.itemSubHeaderText}
          data-testid="draw-property-value"
        >
          Property Details
        </p>
        {selectedProperty.length ? (
          <p
            className={classes.itemValueText}
            data-testid="draw-property-options"
          >
            {getPropertyView()}
          </p>
        ) : (
          <p
            className={classes.itemValueText}
            data-testid="draw-property-blank"
          >
            __ __
          </p>
        )}
      </div>
    );
  }
  const isLoanIdEmpty = selectedLoanId === "";
  return (
    <Grid>
      <p
        className={`${classes.headerLoanID} ${
          isLoanIdEmpty ? classes.disabledHeaderText : classes.headerLoanID
        }`}
      >
        Property Details{" "}
        <span
          className={`${classes.asterick} ${
            isLoanIdEmpty ? classes.hiddenAsterick : classes.asterick
          }`}
        >
          *
        </span>
      </p>
      <div>
        <FormControl variant="outlined" className={classes.formControlOption}>
          <Select
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left"
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left"
              }
            }}
            value={selectedPropertyValue}
            displayEmpty
            disabled={isLoanIdEmpty}
            labelId="select-property"
            id="demo-simple-select-outlined"
            placeholder="Select"
            data-testid="draw-property-dropdown"
            className={classes.selectRoot}
            onChange={(event) => handlePropertyChange(event.target.value)}
            input={<OutlinedInput label="Tag" />}
            renderValue={(value: any) => {
              const address = getPropertyRenderAddress(
                value,
                propertyDetailsOptions,
                selectedProperty
              );
              if (address == null) {
                return <span style={{ color: "#9e9e9e" }}>Select</span>;
              }
              return address;
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {propertyDetailsOptions?.length > 0 &&
              propertyDetailsOptions.map((item: any, index: number) => (
                <MenuItem
                  key={item?.propertyId ?? index}
                  value={String(index)}
                >
                  <ListItemText primary={item.propertyAddress} />
                </MenuItem>
              ))}
          </Select>
          {isSubmitClicked ? showErrorMsg("selectedProperty") : null}
        </FormControl>
      </div>
    </Grid>
  );
}

function OriginatorOnlyFields({ fieldWidth, classes, drawObj }: any) {
  return (
    <>
      <FieldRow fieldWidth={fieldWidth} classes={classes}>
        <ReadOnlyDrawField
          classes={classes}
          label="Funding Channel"
          value={drawObj?.fundingChannel}
          valueTestId="toorak-fundingChannel-value"
          blankTestId="toorak-fundingChannel-blank"
        />
      </FieldRow>
      <FieldRow fieldWidth={fieldWidth} classes={classes}>
        <ReadOnlyDrawField
          classes={classes}
          label="Seller Name"
          value={drawObj?.sellerName}
          valueTestId="toorak-sellerName-value"
          blankTestId="toorak-sellerName-blank"
        />
      </FieldRow>
      <FieldRow fieldWidth={fieldWidth} classes={classes}>
        <ReadOnlyDrawField
          classes={classes}
          label="Broker Name"
          value={drawObj?.brokerName}
          valueTestId="toorak-brokerName-value"
          blankTestId="toorak-brokerName-blank"
        />
      </FieldRow>
      <FieldRow fieldWidth={fieldWidth} classes={classes}>
        <ReadOnlyDrawField
          classes={classes}
          label="Originating Entity"
          value={drawObj?.originatingEntity}
          valueTestId="toorak-originatingEntity-value"
          blankTestId="toorak-originatingEntity-blank"
        />
      </FieldRow>
    </>
  );
}

export const DrawLoanInformation = ({
  isSubmitClicked,
  showErrorMsg,
  isBulkDraw,
  fieldWidth,
  setSnackBarMsg,
  setOpenSnackBar
}: any) => {
  const classes = useDrawsStyles();
  const isOrig = isOrigUser();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const storeRef = useRef(store);
  storeRef.current = store;
  const lastDrawSyncKeyRef = useRef<string>("");
  const lastLoanOptionsLengthRef = useRef<number>(0);
  const [availableLoanIds, setAvailableLoanIds] = useState<any>([]);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<any[]>([]);
  const [drawObj, setDrawObj] = useState<any>({});
  const { draft } = DrawStage;
  const isDraft =
    drawObj.stage === draft && !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW);
  const { drawId } = useParams<any>() as any;

  const {
    loanIds,
    drawDetails,
    selectedBankDetails,
    propertyDetailsOptions
  } = useSelector<RootState, DrawRequestStore>(
    (state) => state.drawRequestStore,
    shallowEqual
  );

  useEffect(() => {
    const len = loanIds?.length ?? 0;
    if (len === 0) {
      if (lastLoanOptionsLengthRef.current !== 0) {
        lastLoanOptionsLengthRef.current = 0;
        setAvailableLoanIds([]);
      }
      return;
    }
    if (lastLoanOptionsLengthRef.current === len) return;
    lastLoanOptionsLengthRef.current = len;
    setAvailableLoanIds(buildLoanIdOptions(loanIds));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [loanIds?.length]);

  useEffect(() => {
    if (!drawDetails) return;
    const syncKey = `${drawDetails?.primaryLoanId ?? ""}|${drawDetails?.stage ?? ""}|${drawDetails?.propertyDetails?.length ?? 0}`;
    if (lastDrawSyncKeyRef.current === syncKey) return;
    lastDrawSyncKeyRef.current = syncKey;
    setDrawObj(drawDetails);
    setSelectedLoanId((drawDetails?.primaryLoanId as string) ?? null);
    setSelectedProperty(getSyncedPropertySelection(drawDetails));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    drawDetails?.primaryLoanId,
    drawDetails?.stage,
    drawDetails?.propertyDetails?.length
  ]);

  const handleLoanIdChange = (selectedOption: any) => {
    if (!selectedOption) {
      setSelectedLoanId(null);
      return;
    }
    const { primaryLoanId, toorakLoanId } = resolveLoanIdsFromOption(
      selectedOption,
      drawDetails?.toorakLoanId
    );
    setSelectedLoanId(primaryLoanId);
    dispatch(
      getDrawDetailsbyLoanId(
        isOrig,
        toorakLoanId,
        drawId,
        setSnackBarMsg,
        setOpenSnackBar,
        {
          isChanged: true,
          drawAmountDetails: drawDetails.drawAmountDetails,
          drawObj
        }
      )
    );
  };

  const handlePropertyChange = (value: any) => {
    const option = resolvePropertyOption(
      value,
      propertyDetailsOptions,
      selectedProperty
    );
    const newSelection = option ? [option] : [];
    setSelectedProperty(newSelection);
    dispatch(updateDrawSelectedProp(newSelection));
  };

  const selectedPropertyValue = getSelectedPropertySelectValue(
    selectedProperty,
    propertyDetailsOptions
  );

  useDraftDrawAutosave({
    dispatch,
    storeRef,
    drawId,
    isOrig,
    draft,
    drawDetails,
    selectedBankDetails,
    selectedProperty
  });

  const getPropertyView = () => {
    const AddressString = {
      row: { properties: getPropertyAddresses(selectedProperty) }
    };
    return propertFormatter(
      { row: AddressString.row, key: "propertyDetails" },
      classes,
      true
    );
  };

  return (
    <Grid
      item
      container
      xs={12}
      data-testid="draw-view-itemContainer"
      className={classes.itemContainer}
    >
      <Grid
        item
        container
        xs={10}
        className={classes.itemHeaderText}
        data-testid="draw-view-itemHeaderText"
      >
        Loan Information
      </Grid>
      <Grid
        item
        container
        xs={12}
        data-testid="draw-view-fieldContainer"
        className={classes.fieldContainer}
      >
        <FieldRow
          fieldWidth={fieldWidth}
          classes={classes}
          testId="draw-view-primary-loan"
        >
          <PrimaryLoanIdSection
            isEditable={isDraft && !isBulkDraw}
            classes={classes}
            availableLoanIds={availableLoanIds}
            selectedLoanId={selectedLoanId}
            drawDetails={drawDetails}
            drawObj={drawObj}
            isSubmitClicked={isSubmitClicked}
            showErrorMsg={showErrorMsg}
            handleLoanIdChange={handleLoanIdChange}
          />
        </FieldRow>
        <FieldRow
          fieldWidth={fieldWidth}
          classes={classes}
          testId="draw-property-details"
        >
          <PropertyDetailsSection
            isDraft={isDraft}
            classes={classes}
            selectedLoanId={selectedLoanId}
            selectedProperty={selectedProperty}
            selectedPropertyValue={selectedPropertyValue}
            propertyDetailsOptions={propertyDetailsOptions}
            isSubmitClicked={isSubmitClicked}
            showErrorMsg={showErrorMsg}
            handlePropertyChange={handlePropertyChange}
            getPropertyView={getPropertyView}
          />
        </FieldRow>
        <FieldRow
          fieldWidth={fieldWidth}
          classes={classes}
          testId="draw-toorak-loanId"
        >
          <ReadOnlyDrawField
            classes={classes}
            label="Toorak Loan ID"
            value={drawObj?.toorakLoanId}
            valueTestId="toorak-loanId-value"
            blankTestId="toorak-loanId-blank"
          />
        </FieldRow>
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Originator Name"
            value={drawObj?.originatorName}
            valueTestId="toorak-originatorName-value"
            blankTestId="toorak-originatorName-blank"
          />
        </FieldRow>
        {!isOrig && (
          <OriginatorOnlyFields
            fieldWidth={fieldWidth}
            classes={classes}
            drawObj={drawObj}
          />
        )}
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Servicer ID"
            value={drawObj?.servicerId}
            valueTestId="toorak-servicerId-value"
            blankTestId="toorak-servicerId-blank"
          />
        </FieldRow>
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Servicer Name"
            value={drawObj?.servicerName}
            valueTestId="toorak-servicerName-value"
            blankTestId="toorak-servicerName-blank"
          />
        </FieldRow>
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Loan Type"
            value={drawObj?.loanType}
            valueTestId="toorak-loanType-value"
            blankTestId="toorak-loanType-blank"
          />
        </FieldRow>
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Draw Number"
            value={drawObj?.drawNumber}
            valueTestId="toorak-drawNumber-value"
            blankTestId="toorak-drawNumber-blank"
          />
        </FieldRow>
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Borrower Name"
            value={drawObj?.borrowerEntityName}
            valueTestId="toorak-borrowerEntityName-value"
            blankTestId="toorak-borrowerEntityName-blank"
          />
        </FieldRow>
        <FieldRow fieldWidth={fieldWidth} classes={classes}>
          <ReadOnlyDrawField
            classes={classes}
            label="Address"
            value={drawObj?.borrowerAddress}
            valueTestId="toorak-borrowerAddress-value"
            blankTestId="toorak-borrowerAddress-blank"
          />
        </FieldRow>
      </Grid>
    </Grid>
  );
};
